import React, { useState } from 'react'
import {
  Card,
  Button,
  Space,
  Tag,
  Row,
  Col,
  Tabs,
  Input,
  Form,
  Select,
  message,
  Modal,
  Avatar,
  Tooltip,
  Badge,
  Divider,
  Timeline,
  Alert,
  Popconfirm,
  DatePicker,
} from 'antd'
import {
  EditOutlined,
  DeleteOutlined,
  ArrowRightOutlined,
  WhatsAppOutlined,
  MailOutlined,
  PhoneOutlined,
  FileTextOutlined,
  SendOutlined,
  ArrowLeftOutlined,
  UserSwitchOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  EnvironmentOutlined,
  ThunderboltOutlined,
  TeamOutlined,
  CalendarOutlined,
  MessageOutlined,
  VideoCameraOutlined,
  FileProtectOutlined,
} from '@ant-design/icons'
import { useNavigate, useParams } from 'react-router-dom'
import { LeadStage, Lead, Discussion, ProposalStatus } from '@/types'
import { leadStore } from './leadStore'
import { api, ApiError } from '@/services/api'
import { htmlToPlainText } from '@/utils/htmlToPlainText'
import { annualToMonthlyUnits, formatUnitsKWh } from '@/utils/numberFormat'
import { proposalStore } from '../Proposal/proposalStore'
import { useMasterData } from '@/context/MasterDataContext'
import { useModulePermission } from '@/hooks/useModulePermission'
import { findLeadByGst, findLeadByHtsc, normalizeGst, isValidGstFormat, formatDate } from '@/utils/gst'
import { authStore } from '@/context/auth'
import './LeadList.css'

/* ── Helpers ───────────────────────────────────────── */
const STAGE_CONFIG: Record<LeadStage, { color: string; antColor: string; emoji: string; next?: LeadStage[] }> = {
  [LeadStage.HOT]:  { color: '#DC2626', antColor: 'red',     emoji: '🔴', next: [LeadStage.WARM, LeadStage.COLD, LeadStage.LOST, LeadStage.WON] },
  [LeadStage.WARM]: { color: '#D97706', antColor: 'gold',    emoji: '🟡', next: [LeadStage.HOT, LeadStage.COLD, LeadStage.LOST, LeadStage.WON] },
  [LeadStage.COLD]: { color: '#6B7280', antColor: 'default', emoji: '🔵', next: [LeadStage.WARM, LeadStage.HOT, LeadStage.LOST, LeadStage.WON] },
  [LeadStage.LOST]: { color: '#7C3AED', antColor: 'purple',  emoji: '⚫', next: [LeadStage.COLD, LeadStage.WARM, LeadStage.HOT, LeadStage.WON] },
  [LeadStage.WON]:  { color: '#059669', antColor: 'green',   emoji: '🏆', next: [LeadStage.HOT, LeadStage.WARM, LeadStage.COLD, LeadStage.LOST] },
}

// L-03: maps the stored lost-reason code back to a readable label for display
const LOST_REASON_LABELS: Record<string, string> = {
  price_high:      'Price too high',
  competitor:      'Competitor selected',
  no_budget:       'No budget',
  no_requirement:  'No requirement now',
  not_contactable: 'Not contactable',
  technical:       'Technical mismatch',
  other:           'Other',
}

const DISC_CONFIG = {
  whatsapp: { icon: <WhatsAppOutlined />, color: '#25D366', bg: '#F0FDF4', label: 'WhatsApp', borderColor: '#25D366' },
  email:    { icon: <MailOutlined />,     color: '#2563EB', bg: '#EFF6FF', label: 'Email',    borderColor: '#2563EB' },
  call:     { icon: <PhoneOutlined />,    color: '#D97706', bg: '#FFFBEB', label: 'Call',     borderColor: '#D97706' },
  note:     { icon: <FileTextOutlined />, color: '#6B7280', bg: '#F9FAFB', label: 'Note',     borderColor: '#E5E7EB' },
  visit:    { icon: <EnvironmentOutlined />, color: '#0D9488', bg: '#F0FDFA', label: 'Visit', borderColor: '#14B8A6' },
  sms:      { icon: <MessageOutlined />,  color: '#0891B2', bg: '#ECFEFF', label: 'SMS',      borderColor: '#06B6D4' },
  video:    { icon: <VideoCameraOutlined />, color: '#7C3AED', bg: '#F5F3FF', label: 'Video Call', borderColor: '#8B5CF6' },
  expomeet: { icon: <TeamOutlined />, color: '#9333EA', bg: '#FAF5FF', label: 'Expomeet Visit', borderColor: '#A855F7' },
}

/* ── Communication Templates ───────────────────────── */
const WHATSAPP_TEMPLATES = [
  {
    key: 'welcome',
    label: 'Welcome / Initial Contact',
    text: 'Hello {{customerName}} from {{companyName}},\n\nThank you for contacting Sakthi Powers. We are excited to assist you with your green energy requirements. A sales representative will contact you shortly to explain our consumer/generator solutions.\n\nRegards,\nSakthi Powers Team',
  },
  {
    key: 'introduction',
    label: 'Introduction & Cost Savings',
    text: 'Dear {{customerName}},\n\nThis is {{salesUser}} from Sakthi Powers. We help industries optimize electricity costs. For your {{voltage}} connection with {{demand}} demand, we can offer custom wind/solar tariff structures that can reduce your monthly grid bills by up to 15%.\n\nLet me know a convenient time to discuss this briefly.\n\nBest regards,\n{{salesUser}}',
  },
  {
    key: 'followup',
    label: 'Follow-up on requirements',
    text: 'Hi {{customerName}},\n\nJust following up on our recent conversation regarding the solar power grid tie-up for {{companyName}}. Please let us know if you have any questions about the tariff models or commercial terms.\n\nRegards,\n{{salesUser}}',
  },
]

const EMAIL_TEMPLATES = [
  {
    key: 'introduction',
    label: 'Formal Company Introduction',
    subject: 'Sakthi Powers — Commercial & Industrial Energy Cost Optimization',
    body: 'Dear {{customerName}},\n\nGreeting from Sakthi Powers!\n\nWe are a leading renewable energy transaction manager in Tamil Nadu, facilitating power allocation between wind/solar generators and high-tension (HT) consumers.\n\nWe noticed that {{companyName}} has a sanctioned demand of {{demand}} at {{voltage}} drawal voltage. We can help you transition to green power and save significantly on your monthly electricity bill under Group Captive rules.\n\nI have attached our company profile for your review. I look forward to scheduling a quick call to discuss how we can partner.\n\nWarm regards,\n{{salesUser}}\nSakthi Powers',
  },
  {
    key: 'doc_request',
    label: 'HTSC Document Request Checklist',
    subject: 'Required Documents for Tariff Analysis — Sakthi Powers',
    body: 'Dear {{customerName}},\n\nThank you for your interest in Sakthi Powers.\n\nTo prepare a detailed cost-saving proposal for {{companyName}}, could you please share the following details/documents:\n\n1. Copy of your latest 3 months grid electricity bills.\n2. HTSC Number ({{htscNumber}}).\n3. Connected/Sanctioned demand verification.\n\nOnce received, our technical team will run an allocation simulation and submit a custom commercial proposal within 24 hours.\n\nWarm regards,\n{{salesUser}}\nSakthi Powers',
  },
  {
    key: 'proposal_followup',
    label: 'Proposal Review Follow-up',
    subject: 'Follow-up: Green Power Tariff Proposal for {{companyName}}',
    body: 'Dear {{customerName}},\n\nI hope you are doing well.\n\nI wanted to follow up on the commercial proposal we shared last week for {{companyName}}. Under the proposed group captive structure, your tariff savings will be approximately 15% compared to the standard grid rate.\n\nLet me know if we can schedule a quick 10-minute call tomorrow to answer any queries or explain the commercial terms.\n\nBest regards,\n{{salesUser}}\nSakthi Powers',
  },
]

const interpolateTemplate = (
  text: string,
  lead: Lead,
  salesUser: string,
  sender?: { phone?: string; email?: string }
): string => {
  if (!text) return ''
  return text
    .replace(/{{customerName}}/g, lead.customerName || '')
    .replace(/{{contactPerson}}/g, lead.customerName || '')
    .replace(/{{companyName}}/g, lead.companyName || '')
    .replace(/{{demand}}/g, lead.sanctionedDemand ? `${lead.sanctionedDemand} ${lead.demandUnit || 'KW'}` : '')
    .replace(/{{voltage}}/g, lead.drawlVoltage || '')
    .replace(/{{htscNumber}}/g, lead.htscNumber || 'HTSC Number')
    .replace(/{{salesUser}}/g, salesUser)
    .replace(/{{senderName}}/g, salesUser)
    .replace(/{{senderPhone}}/g, sender?.phone || '')
    .replace(/{{senderEmail}}/g, sender?.email || '')
}

// Looks up the assigned sales rep's phone/email from Employee Master, so
// {{senderPhone}}/{{senderEmail}} in a template resolve to the real person
// sending the message, not the recipient.
const findSenderContact = (
  salesUser: string,
  employees: { name: string; phone?: string; email?: string }[]
): { phone: string; email: string } => {
  const emp = employees.find((e) => e.name === salesUser)
  return { phone: emp?.phone || '', email: emp?.email || '' }
}

/* ──────────────────────────────────────────────────── */

const LeadDetail: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const [form] = Form.useForm()

  // Masters: Employee, WhatsApp Templates, Email Templates
  const { employees, whatsappTemplates, emailTemplates } = useMasterData()
  const { canEmail } = useModulePermission('Leads')

  // Dynamic WhatsApp Templates from Master Data
  const combinedWaTemplates = [
    ...(whatsappTemplates || [])
      .filter(t => t.status === 'active')
      .map(t => ({
        key: t.id,
        label: `${t.name} (${t.category})`,
        text: [t.headerText, t.bodyText, t.footerText].filter(Boolean).join('\n\n'),
      })),
  ]

  // Dynamic Email Templates from Master Data
  const combinedEmailTemplates = [
    ...(emailTemplates || [])
      .filter(t => t.status === 'active')
      .map(t => ({
        key: t.id,
        label: `${t.name} (${t.category})`,
        subject: t.subject,
        body: t.bodyHtml ? htmlToPlainText(t.bodyHtml) : '',
      })),
  ]

  // Grouped by role so you can choose a role, then the person to assign
  const salesUserOptions = Object.entries(
    employees
      .filter(u => u.status === 'active')
      .reduce<Record<string, { name: string }[]>>((acc, u) => {
        (acc[u.role] = acc[u.role] || []).push(u)
        return acc
      }, {})
  ).map(([role, list]) => ({
    label: role,
    options: list.map(u => ({ label: u.name, value: u.name })),
  }))

  // State
  const [lead, setLead] = useState<Lead | null>(null)
  const [stageModalOpen, setStageModalOpen] = useState(false)
  const [lostModalOpen, setLostModalOpen] = useState(false)
  const [reassignModalOpen, setReassignModalOpen] = useState(false)
  const [gstModalOpen, setGstModalOpen] = useState(false)
  const [pendingStage, setPendingStage] = useState<LeadStage | null>(null)
  const [lostForm] = Form.useForm()
  const [gstForm] = Form.useForm()
  const [discussions, setDiscussions] = useState<Discussion[]>([])

  // Communication Modals States
  const [whatsappModalOpen, setWhatsappModalOpen] = useState(false)
  const [emailModalOpen, setEmailModalOpen] = useState(false)
  const [emailSending, setEmailSending] = useState(false)
  const [callModalOpen, setCallModalOpen] = useState(false)
  const [visitModalOpen, setVisitModalOpen] = useState(false)
  const [expomeetModalOpen, setExpomeetModalOpen] = useState(false)
  const [followupModalOpen, setFollowupModalOpen] = useState(false)

  // Forms
  const [whatsappForm] = Form.useForm()
  const [emailForm] = Form.useForm()
  const [callForm] = Form.useForm()
  const [visitForm] = Form.useForm()
  const [expomeetForm] = Form.useForm()
  const [followupForm] = Form.useForm()

  React.useEffect(() => {
    if (id) {
      if (!leadStore.loaded) {
        leadStore.refresh().then(() => {
          const found = leadStore.getLead(id)
          if (found) {
            setLead(found)
            setDiscussions(found.discussionHistory || [])
          } else {
            message.error('Lead not found')
            navigate('/leads')
          }
        })
      } else {
        const found = leadStore.getLead(id)
        if (found) {
          setLead(found)
          setDiscussions(found.discussionHistory || [])
        } else {
          message.error('Lead not found')
          navigate('/leads')
        }
      }
    }
  }, [id, navigate])

  if (!lead) {
    return (
      <div style={{ padding: '48px', textAlign: 'center', background: '#f5f5f5', borderRadius: '8px' }}>
        <h3>Loading lead details...</h3>
      </div>
    )
  }

  const cfg = STAGE_CONFIG[lead.stage] || STAGE_CONFIG[LeadStage.COLD]
  const nextStages = cfg.next ?? []

  /* ── Stage change logic ─────────────────────────── */
  const handleStageChange = (newStage: LeadStage) => {
    if (newStage === LeadStage.LOST) {
      setPendingStage(newStage)
      setLostModalOpen(true)
    } else {
      setPendingStage(newStage)
      setStageModalOpen(true)
    }
  }

  const confirmStageChange = () => {
    if (!pendingStage) return
    const updated = { ...lead, stage: pendingStage }
    leadStore.saveLead(updated)
    setLead(updated)
    message.success(`Stage changed to ${pendingStage}`)
    setStageModalOpen(false)
    setPendingStage(null)
  }

  const confirmLostStage = (values: any) => {
    const updated = {
      ...lead,
      stage: LeadStage.LOST,
      lostReason: values.lostReason,
      lostNotes: values.lostNotes,
    }
    leadStore.saveLead(updated)
    setLead(updated)
    message.success('Lead marked as Lost. Retained for future reactivation.')
    setLostModalOpen(false)
    lostForm.resetFields()
  }

  /* ── Modal Opening & Auto-Filling ────────────────── */
  const openVisitModal = () => {
    setVisitModalOpen(true)
  }

  const openExpomeetModal = () => {
    expomeetForm.resetFields()
    setExpomeetModalOpen(true)
  }

  const openWhatsappModal = () => {
    const firstTpl = combinedWaTemplates[0]
    const templateText = firstTpl?.text || ''
    const interpolated = interpolateTemplate(templateText, lead, lead.assignedTo || 'Our Team', findSenderContact(lead.assignedTo || '', employees))
    whatsappForm.setFieldsValue({
      template: firstTpl?.key || '',
      message: interpolated,
    })
    setWhatsappModalOpen(true)
  }

  const openEmailModal = () => {
    const firstTpl = combinedEmailTemplates[0]
    if (firstTpl) {
      const senderContact = findSenderContact(lead.assignedTo || '', employees)
      const interpolatedSubject = interpolateTemplate(firstTpl.subject, lead, lead.assignedTo || 'Our Team', senderContact)
      const interpolatedBody = interpolateTemplate(firstTpl.body, lead, lead.assignedTo || 'Our Team', senderContact)
      emailForm.setFieldsValue({
        template: firstTpl.key,
        subject: interpolatedSubject,
        body: interpolatedBody,
      })
    }
    setEmailModalOpen(true)
  }

  const openCallModal = () => {
    callForm.resetFields()
    setCallModalOpen(true)
  }

  /* ── Template Select Listeners ───────────────────── */
  const handleWaTemplateChange = (val: string) => {
    const templateText = combinedWaTemplates.find(t => t.key === val)?.text || ''
    const interpolated = interpolateTemplate(templateText, lead, lead.assignedTo || 'Our Team', findSenderContact(lead.assignedTo || '', employees))
    whatsappForm.setFieldsValue({
      message: interpolated,
    })
  }

  const handleEmailTemplateChange = (val: string) => {
    const template = combinedEmailTemplates.find(t => t.key === val)
    if (template) {
      const senderContact = findSenderContact(lead.assignedTo || '', employees)
      const interpolatedSubject = interpolateTemplate(template.subject, lead, lead.assignedTo || 'Our Team', senderContact)
      const interpolatedBody = interpolateTemplate(template.body, lead, lead.assignedTo || 'Our Team', senderContact)
      emailForm.setFieldsValue({
        subject: interpolatedSubject,
        body: interpolatedBody,
      })
    }
  }

  /* ── Communication actions submit ────────────────── */
  // Every discussion-history entry below needs to know who actually did it.
  // Silently falling back to a placeholder string ("Current User") when the
  // session's name is missing would save a business record with WRONG
  // attribution and no visible sign anything was off — worse than blocking
  // the action outright, so this fails loudly instead.
  const requireActorName = (): string | null => {
    const name = authStore.getUser()?.name
    if (!name) {
      message.error('Your session looks stale — please refresh the page and sign in again before logging this.')
      return null
    }
    return name
  }

  const handleWhatsappSend = (values: any) => {
    const actor = requireActorName()
    if (!actor) return

    // 1. Redirect to WhatsApp API
    const cleanPhone = (lead.contactNumber || '').replace(/[^0-9]/g, '')
    const formattedPhone = cleanPhone.length === 10 ? `91${cleanPhone}` : cleanPhone
    const encodedText = encodeURIComponent(values.message)
    const waUrl = `https://api.whatsapp.com/send?phone=${formattedPhone}&text=${encodedText}`
    window.open(waUrl, '_blank')

    // 2. Add to timeline
    const templateName = combinedWaTemplates.find(t => t.key === values.template)?.label || values.template
    const newDisc: Discussion = {
      id: `D${Date.now()}`,
      type: 'whatsapp',
      content: `WhatsApp message sent [Template: ${templateName}]\n\n${values.message}`,
      createdBy: actor,
      createdAt: new Date(),
      nextFollowUpDate: values.nextFollowup ? values.nextFollowup.toISOString() : undefined,
    }
    const updatedDiscussions = [newDisc, ...discussions]
    const updatedLead = {
      ...lead,
      discussionHistory: updatedDiscussions,
    }
    leadStore.saveLead(updatedLead)
    setLead(updatedLead)
    setDiscussions(updatedDiscussions)
    
    setWhatsappModalOpen(false)
    message.success('WhatsApp redirect triggered — logged activity')
  }

  const handleEmailSend = async (values: any) => {
    const actor = requireActorName()
    if (!actor) return

    if (!lead.email) {
      message.error('This lead has no email address on file.')
      return
    }

    setEmailSending(true)
    try {
      await api.emailLead(lead.id, { subject: values.subject, body: values.body })

      const templateName = combinedEmailTemplates.find(t => t.key === values.template)?.label || values.template
      const newDisc: Discussion = {
        id: `D${Date.now()}`,
        type: 'email',
        content: `Email sent [Template: ${templateName}]\nSubject: ${values.subject}\n\n${values.body}`,
        createdBy: actor,
        createdAt: new Date(),
        nextFollowUpDate: values.nextFollowup ? values.nextFollowup.toISOString() : undefined,
      }
      const updatedDiscussions = [newDisc, ...discussions]
      const updatedLead = {
        ...lead,
        discussionHistory: updatedDiscussions,
      }
      leadStore.saveLead(updatedLead)
      setLead(updatedLead)
      setDiscussions(updatedDiscussions)

      setEmailModalOpen(false)
      message.success(`Email sent to ${lead.email}`)
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : 'Could not send the email. Please try again.')
    } finally {
      setEmailSending(false)
    }
  }

  const handleCallLog = (values: any) => {
    const actor = requireActorName()
    if (!actor) return

    const newDisc: Discussion = {
      id: `D${Date.now()}`,
      type: 'call',
      content: `Call Logged [Outcome: ${values.outcome}]\nNotes: ${values.notes}`,
      createdBy: actor,
      createdAt: new Date(),
      nextFollowUpDate: values.nextFollowup ? values.nextFollowup.toISOString() : undefined,
    }
    const updatedDiscussions = [newDisc, ...discussions]
    const updatedLead = {
      ...lead,
      discussionHistory: updatedDiscussions,
    }
    leadStore.saveLead(updatedLead)
    setLead(updatedLead)
    setDiscussions(updatedDiscussions)

    setCallModalOpen(false)
    message.success('Call logged successfully')
  }

  const handleFollowupLog = (values: any) => {
    const actor = requireActorName()
    if (!actor) return

    const newDisc: Discussion = {
      id: `D${Date.now()}`,
      type: 'note',
      content: `Follow-up Action Logged:\n${values.notes}`,
      createdBy: actor,
      createdAt: new Date(),
      nextFollowUpDate: values.nextFollowup ? values.nextFollowup.toISOString() : undefined,
    }
    const updatedDiscussions = [newDisc, ...discussions]
    const updatedLead = {
      ...lead,
      discussionHistory: updatedDiscussions,
    }
    leadStore.saveLead(updatedLead)
    setLead(updatedLead)
    setDiscussions(updatedDiscussions)

    setFollowupModalOpen(false)
    message.success('Follow-up logged successfully')
  }

  const handleVisitLog = (values: any) => {
    if (!requireActorName()) return
    if (!navigator.geolocation) {
      saveVisit(values, undefined)
      return
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        saveVisit(values, { lat: position.coords.latitude, lng: position.coords.longitude })
      },
      (error) => {
        message.warning('Could not get precise location. Saving visit without location.')
        saveVisit(values, undefined)
      },
      { timeout: 10000, enableHighAccuracy: true }
    )
  }

  const saveVisit = (values: any, location: { lat: number, lng: number } | undefined) => {
    // Re-checked (not just in handleVisitLog) since the geolocation prompt
    // this is called after can sit open for up to 10s — long enough for a
    // session to lapse in between.
    const actor = requireActorName()
    if (!actor) return
    const newDisc: Discussion = {
      id: `D${Date.now()}`,
      type: 'visit',
      content: `Site Visit Logged\nNotes: ${values.notes}`,
      createdBy: actor,
      createdAt: new Date(),
      location: location,
      nextFollowUpDate: values.nextFollowup ? values.nextFollowup.toISOString() : undefined,
    }
    const updatedDiscussions = [newDisc, ...discussions]
    const updatedLead = { ...lead, discussionHistory: updatedDiscussions }
    leadStore.saveLead(updatedLead)
    setLead(updatedLead)
    setDiscussions(updatedDiscussions)
    setVisitModalOpen(false)
    visitForm.resetFields()
    message.success('Visit logged successfully')
  }

  const handleExpomeetLog = (values: any) => {
    if (!requireActorName()) return
    if (!navigator.geolocation) {
      saveExpomeet(values, undefined)
      return
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        saveExpomeet(values, { lat: position.coords.latitude, lng: position.coords.longitude })
      },
      () => {
        message.warning('Could not get precise location. Saving Expomeet visit without location.')
        saveExpomeet(values, undefined)
      },
      { timeout: 10000, enableHighAccuracy: true }
    )
  }

  const saveExpomeet = (values: any, location: { lat: number, lng: number } | undefined) => {
    // Re-checked (not just in handleExpomeetLog) since the geolocation prompt
    // this is called after can sit open for up to 10s — long enough for a
    // session to lapse in between.
    const actor = requireActorName()
    if (!actor) return
    const newDisc: Discussion = {
      id: `D${Date.now()}`,
      type: 'expomeet',
      content: `Expomeet Visit — ${values.eventName}\nNotes: ${values.notes}`,
      createdBy: actor,
      createdAt: new Date(),
      location: location,
      nextFollowUpDate: values.nextFollowup ? values.nextFollowup.toISOString() : undefined,
    }
    const updatedDiscussions = [newDisc, ...discussions]
    const updatedLead = { ...lead, discussionHistory: updatedDiscussions }
    leadStore.saveLead(updatedLead)
    setLead(updatedLead)
    setDiscussions(updatedDiscussions)
    setExpomeetModalOpen(false)
    expomeetForm.resetFields()
    message.success('Expomeet visit logged successfully')
  }

  /* ── Add discussion ─────────────────────────────── */
  const onAddDiscussion = (values: any) => {
    const actor = requireActorName()
    if (!actor) return

    const newDisc: Discussion = {
      id: `D${Date.now()}`,
      type: values.type,
      content: values.content,
      createdBy: actor,
      createdAt: new Date(),
    }
    const updatedDiscussions = [newDisc, ...discussions]
    const updatedLead = {
      ...lead,
      discussionHistory: updatedDiscussions,
    }
    leadStore.saveLead(updatedLead)
    setLead(updatedLead)
    setDiscussions(updatedDiscussions)
    message.success('Discussion note added')
    form.resetFields()
  }

  /* ── Reassign ───────────────────────────────────── */
  const onReassign = (values: any) => {
    const updated = { ...lead, assignedTo: values.assignedTo }
    leadStore.saveLead(updated)
    setLead(updated)
    message.success(`Lead reassigned to ${values.assignedTo}`)
    setReassignModalOpen(false)
  }

  /* ── HTSC / GST update (clears the 2-day pending reminder) ────────── */
  const onUpdateGst = (values: any) => {
    const leads = leadStore.getLeads()
    const htsc = String(values.htscNumber || '').trim()
    const gst = normalizeGst(values.gstNumber)

    const htscDup = htsc ? findLeadByHtsc(htsc, leads, lead.id) : undefined
    if (htscDup) {
      message.error(`HTSC Number ${htsc} already belongs to "${htscDup.companyName}". HTSC Numbers can't be duplicated.`)
      return
    }
    const gstDup = gst ? findLeadByGst(gst, leads, lead.id) : undefined
    if (gstDup) {
      message.error(`GST Number ${gst} already belongs to "${gstDup.companyName}". GST Numbers can't be duplicated.`)
      return
    }

    // gstStatus / gstVerifyDueDate / isDeactive are recomputed from the saved
    // record by leadStore, so this only supplies the two raw values.
    const updated = { ...lead, htscNumber: htsc || undefined, gstNumber: gst || undefined }
    leadStore.saveLead(updated)
    setLead(leadStore.getLead(lead.id) || updated)
    message.success(htsc && gst ? 'HTSC & GST Numbers updated.' : 'Lead updated — the remaining number is still pending.')
    setGstModalOpen(false)
  }

  /* ── Render ─────────────────────────────────────── */
  return (
    <div className="lead-detail-container">

      {/* ── Page Header ──────────────────────────────── */}
      <div className="ld-header">
        <div className="ld-header-left">
          <Button
            icon={<ArrowLeftOutlined />}
            type="text"
            onClick={() => navigate('/leads')}
            className="lf-back-btn"
          />
          <Avatar
            size={48}
            style={{
              background: 'linear-gradient(135deg, #1E3A8A, #2563EB)',
              fontSize: 18,
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            {lead.companyName.slice(0, 2).toUpperCase()}
          </Avatar>
          <div>
            <h2 className="ld-company-name">{lead.companyName}</h2>
            <div className="ld-meta-row">
              {/* The lead number is the record id (Code Master series "L") —
                  shown here because it's what staff quote on the phone and in
                  email, not something they should have to open a tab to find. */}
              <Tag
                color="blue"
                style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', margin: 0 }}
              >
                Lead No. {lead.id}
              </Tag>
              <Tag color={cfg.antColor}>{cfg.emoji} {lead.stage}</Tag>
              <span className="ld-meta-item">
                <TeamOutlined /> {lead.assignedTo || 'Unassigned'}
              </span>
              <span className="ld-meta-item">
                <CalendarOutlined /> Created {lead.createdAt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
              </span>
              <Tag color={lead.leadType === 'Consumer' ? 'blue' : 'gold'}>
                {lead.leadType === 'Consumer' ? '👤' : '⚡'} {lead.leadType}
              </Tag>
            </div>
          </div>
        </div>

        <Space wrap>
          <Button icon={<UserSwitchOutlined />} onClick={() => setReassignModalOpen(true)}>
            Reassign
          </Button>
          <Button icon={<EditOutlined />} onClick={() => navigate(`/leads/${lead.id}/edit`)}>
            Edit
          </Button>
          <Popconfirm
            title="Delete Lead"
            description="Are you sure? This cannot be undone."
            onConfirm={async () => {
              try {
                await leadStore.deleteLead(lead.id)
                message.success('Lead deleted')
                navigate('/leads')
              } catch {
                message.error('Could not delete lead — please sign out and log in again.')
              }
            }}
            okText="Delete" cancelText="Cancel" okButtonProps={{ danger: true }}
          >
            <Button danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
          {(() => {
            const activeProp = proposalStore.getAll().find(
              (p) =>
                (p.leadId === lead.id ||
                  (p.leadName && p.leadName.trim().toLowerCase() === lead.companyName.trim().toLowerCase())) &&
                p.status !== ProposalStatus.REJECTED &&
                p.status !== ProposalStatus.EXPIRED
            )

            if (activeProp) {
              return (
                <Button
                  type="primary"
                  icon={<FileProtectOutlined />}
                  onClick={() => navigate(`/proposals/${activeProp.id}`)}
                  style={{ background: '#00A651', borderColor: '#00A651' }}
                >
                  View Proposal ({activeProp.referenceNumber || activeProp.id})
                </Button>
              )
            }

            if (lead.stage !== LeadStage.LOST) {
              // Proposal creation is always allowed, even while the lead is
              // Deactive or missing HTSC/GST — Deactive only blocks jumping
              // stage directly to Won/Warm without going through a proposal.
              return (
                <Button
                  type="primary"
                  icon={<ArrowRightOutlined />}
                  onClick={() => navigate('/proposals/new', { state: { fromLead: lead } })}
                  style={{ background: '#00A651', borderColor: '#00A651' }}
                >
                  Convert to Proposal
                </Button>
              )
            }
            return null
          })()}
        </Space>
      </div>

      {/* ── Overdue HTSC / GST Banner ─────────── */}
      {lead.isDeactive && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={`Overdue — ${(lead.missingFields || ['HTSC Number', 'GST Number']).join(' & ')} still not provided`}
          description={
            <div>
              The <strong>2-day update window</strong> for this lead closed on <strong>{lead.gstVerifyDueDate || '-'}</strong>.
              <br />
              <em>The lead is flagged as Deactive so it stays visible for follow-up — it is not blocked, and can still change stage and be converted to a proposal.</em>
              <div style={{ marginTop: 8 }}>
                <Button
                  type="primary"
                  danger
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => { gstForm.setFieldsValue({ htscNumber: lead.htscNumber, gstNumber: lead.gstNumber }); setGstModalOpen(true) }}
                >
                  Update HTSC & GST Numbers
                </Button>
              </div>
            </div>
          }
        />
      )}

      {/* ── Lost lead banner ─────────────────────────── */}
      {lead.stage === LeadStage.LOST && (
        <Alert
          message="This lead is marked as Lost. It is retained for future reactivation."
          description={
            (lead.lostReason || lead.lostNotes) && (
              <div style={{ marginTop: 4 }}>
                {lead.lostReason && (
                  <div>
                    <strong>Reason:</strong>{' '}
                    {LOST_REASON_LABELS[lead.lostReason] || lead.lostReason}
                  </div>
                )}
                {lead.lostNotes && (
                  <div style={{ marginTop: 2 }}>
                    <strong>Notes:</strong> {lead.lostNotes}
                  </div>
                )}
              </div>
            )
          }
          type="warning"
          showIcon
          action={
            <Button size="small" onClick={() => handleStageChange(LeadStage.COLD)}>
              Reactivate
            </Button>
          }
          style={{ marginBottom: 16 }}
        />
      )}

      {/* ── HTSC / GST pending banner — only while the 2-day window is still
          open; once it's overdue the banner above covers it. ── */}
      {lead.gstStatus === 'pending' && !lead.isDeactive && (
        <Alert
          message={`${(lead.missingFields || ['HTSC Number', 'GST Number']).join(' & ')} pending — please update ${lead.gstVerifyDueDate ? `by ${formatDate(new Date(lead.gstVerifyDueDate))} (2 days from lead creation)` : 'within 2 days of lead creation'}.`}
          type="warning"
          showIcon
          action={
            <Button size="small" onClick={() => { gstForm.setFieldsValue({ htscNumber: lead.htscNumber, gstNumber: lead.gstNumber }); setGstModalOpen(true) }}>
              Update Now
            </Button>
          }
          style={{ marginBottom: 16 }}
        />
      )}

      {/* ── Converted to Proposal Banner ─────────────── */}
      {lead.convertedToProposal && (
        <Alert
          type="info"
          showIcon
          message="Converted to Proposal"
          description="This lead has been converted to a proposal."
          style={{ marginBottom: 16 }}
        />
      )}

      {/* ── Stage Change Bar ─────────────────────────── */}
      <Card className="ld-stage-bar">
        <div className="ld-stage-bar-inner">
          <span className="ld-stage-label">Change Stage:</span>
          <Space wrap>
            {nextStages.map((s) => (
              <Button
                key={s}
                size="small"
                onClick={() => handleStageChange(s)}
                danger={s === LeadStage.LOST}
                type={s === LeadStage.WON ? 'primary' : 'default'}
                style={
                  s === LeadStage.WON
                    ? { background: '#00A651', borderColor: '#00A651' }
                    : undefined
                }
              >
                {STAGE_CONFIG[s]?.emoji} Move to {s}
              </Button>
            ))}
          </Space>
        </div>
      </Card>

      {/* ── Communication Action Buttons ─────────────── */}
      <div className="ld-comm-bar">
        <button
          className="ld-comm-btn ld-comm-whatsapp"
          onClick={openWhatsappModal}
        >
          <WhatsAppOutlined />
          <span>WhatsApp</span>
          <small>Send from template</small>
        </button>
        {canEmail && (
          <button
            className="ld-comm-btn ld-comm-email"
            onClick={openEmailModal}
          >
            <MailOutlined />
            <span>Email</span>
            <small>Choose template</small>
          </button>
        )}
        <button
          className="ld-comm-btn ld-comm-call"
          onClick={openCallModal}
        >
          <PhoneOutlined />
          <span>Log Call</span>
          <small>Record activity</small>
        </button>
        <button
          className="ld-comm-btn ld-comm-location"
          onClick={openVisitModal}
        >
          <EnvironmentOutlined />
          <span>Log Visit</span>
          <small>Record field visit</small>
        </button>
        <button
          className="ld-comm-btn ld-comm-expomeet"
          onClick={openExpomeetModal}
        >
          <TeamOutlined />
          <span>Expomeet Visit</span>
          <small>Met at expo / event</small>
        </button>
      </div>

      {/* ── Main Content ─────────────────────────────── */}
      <Row gutter={[16, 16]}>

        {/* Left: Info Cards */}
        <Col xs={24} lg={8}>

          {/* Customer Info */}
          <Card title="Customer Information" className="ld-info-card">
            {[
              { label: 'Lead No.', value: <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{lead.id}</strong> },
              { label: 'Company', value: lead.companyName },
              { label: 'Contact Person', value: lead.customerName },
              { label: 'Email', value: lead.email },
              { label: 'Phone', value: lead.contactNumber },
              { label: 'City', value: lead.city },
              { label: 'PIN Code', value: lead.pinCode },
              { label: 'Address', value: lead.address },
            ].map(({ label, value }) => (
              <div className="ld-info-row" key={label}>
                <span className="ld-info-label">{label}</span>
                <span className="ld-info-value">{value}</span>
              </div>
            ))}
          </Card>

          {/* Technical Details */}
          <Card title="Technical Details" className="ld-info-card">
            {[
              {
                label: 'HTSC Number',
                value: lead.htscNumber || (lead.gstStatus === 'pending' ? <Tag color="orange">Pending</Tag> : '—'),
              },
              { label: 'EDC Name', value: lead.edcName || '—' },
              { label: 'Sanctioned Demand', value: lead.sanctionedDemand ? `${lead.sanctionedDemand} KW` : '—' },
              { label: lead.leadType === 'Generator' ? 'Injection Voltage' : 'Drawal Voltage', value: lead.drawlVoltage || '—' },
              { label: 'Type of Power', value: lead.typeOfPower || '—' },
              { label: 'Mode of Supply', value: lead.modeOfSupply || '—' },
              { label: 'Supply Units / Annum', value: lead.requiredUnitsPerAnnum ? `${lead.requiredUnitsPerAnnum.toLocaleString('en-IN')} kWh` : '—' },
              { label: 'Supply Units / Month', value: lead.requiredUnitsPerAnnum ? `~${formatUnitsKWh(annualToMonthlyUnits(lead.requiredUnitsPerAnnum))}` : '—' },
              { label: 'Lead Source', value: lead.leadSource },
              { label: 'Assigned To', value: lead.assignedTo || 'Unassigned' },
              {
                label: 'GST Number',
                value: lead.gstNumber
                  ? <span style={{ fontFamily: 'monospace' }}>{lead.gstNumber}</span>
                  : lead.gstStatus === 'pending' ? <Tag color="orange">Pending</Tag> : '—',
              },
            ].map(({ label, value }) => (
              <div className="ld-info-row" key={label}>
                <span className="ld-info-label">{label}</span>
                <span className="ld-info-value">{value}</span>
              </div>
            ))}
          </Card>
        </Col>

        {/* Right: Tabs */}
        <Col xs={24} lg={16}>
          <Card className="ld-tabs-card">
            <Tabs
              defaultActiveKey="discussion"
              items={[
                {
                  key: 'discussion',
                  label: (
                    <span>
                      Discussion History{' '}
                      <Badge
                        count={discussions.length}
                        style={{ backgroundColor: '#1E3A8A' }}
                      />
                    </span>
                  ),
                  children: (
                    <div>
                      {/* Add Discussion Form */}
                      <div className="ld-add-disc">
                        <Form form={form} layout="vertical" onFinish={onAddDiscussion}>
                          <Row gutter={[12, 0]}>
                            <Col xs={24} sm={8}>
                              <Form.Item
                                name="type"
                                label="Type"
                                rules={[{ required: true, message: 'Select type' }]}
                              >
                                <Select
                                  placeholder="Select"
                                  options={[
                                    { label: '📞 Call', value: 'call' },
                                    { label: '📧 Email', value: 'email' },
                                    { label: '💬 WhatsApp', value: 'whatsapp' },
                                    { label: '📝 Note', value: 'note' },
                                  ]}
                                />
                              </Form.Item>
                            </Col>
                            <Col xs={24} sm={16}>
                              <Form.Item
                                name="content"
                                label="Notes / Description"
                                rules={[{ required: true, message: 'Add a note' }]}
                              >
                                <Input.TextArea
                                  rows={2}
                                  placeholder="What was discussed? What is the next step?"
                                />
                              </Form.Item>
                            </Col>
                          </Row>
                          <div style={{ textAlign: 'right' }}>
                            <Button type="primary" htmlType="submit" icon={<SendOutlined />}>
                              Add Note
                            </Button>
                          </div>
                        </Form>
                      </div>

                      <Divider style={{ margin: '16px 0' }} />

                      {/* Timeline */}
                      {discussions.length === 0 ? (
                        <div className="ld-empty">No discussions yet. Add the first note above.</div>
                      ) : (
                        <Timeline
                          items={discussions.map((disc) => {
                            const dc = DISC_CONFIG[disc.type as keyof typeof DISC_CONFIG] || DISC_CONFIG.note
                            return {
                              dot: (
                                <div
                                  style={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: '50%',
                                    background: dc.bg,
                                    border: `2px solid ${dc.borderColor}`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: dc.color,
                                    fontSize: 13,
                                  }}
                                >
                                  {dc.icon}
                                </div>
                              ),
                              children: (
                                <div className="ld-disc-item">
                                  <div className="ld-disc-top">
                                    <span
                                      className="ld-disc-badge"
                                      style={{ background: dc.bg, color: dc.color, borderColor: dc.borderColor }}
                                    >
                                      {dc.label}
                                    </span>
                                    <span className="ld-disc-time">
                                      <ClockCircleOutlined />{' '}
                                      {disc.createdAt.toLocaleString('en-IN', {
                                        day: '2-digit', month: 'short', year: 'numeric',
                                        hour: '2-digit', minute: '2-digit',
                                      })}
                                    </span>
                                    <span className="ld-disc-by">— {disc.createdBy}</span>
                                  </div>
                                  <p className="ld-disc-content">{disc.content}</p>
                                  {disc.nextFollowUpDate && (
                                    <div style={{ marginTop: 8, fontSize: 13, color: '#1E3A8A', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                      <span>
                                        <CalendarOutlined style={{ marginRight: 4 }} />
                                        Next Follow-up: {new Date(disc.nextFollowUpDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                                      </span>
                                      <Button size="small" type="primary" onClick={() => {
                                        followupForm.resetFields()
                                        setFollowupModalOpen(true)
                                      }}>
                                        Log Follow-up
                                      </Button>
                                    </div>
                                  )}
                                  {disc.location && (
                                    <div style={{ marginTop: 8 }}>
                                      <a href={`https://www.google.com/maps?q=${disc.location.lat},${disc.location.lng}`} target="_blank" rel="noreferrer">
                                        <EnvironmentOutlined /> View Captured Location
                                      </a>
                                    </div>
                                  )}
                                </div>
                              ),
                            }
                          })}
                        />
                      )}
                    </div>
                  ),
                },
                {
                  key: 'overview',
                  label: 'Lead Overview',
                  children: (
                    <div className="ld-overview">
                      <div className="ld-overview-kv">
                        <span>Lead No.</span><strong>{lead.id}</strong>
                      </div>
                      <div className="ld-overview-kv">
                        <span>Lead Type</span>
                        <Tag color={lead.leadType === 'Consumer' ? 'blue' : 'gold'}>
                          {lead.leadType === 'Consumer' ? '👤' : '⚡'} {lead.leadType}
                        </Tag>
                      </div>
                      <div className="ld-overview-kv">
                        <span>Current Stage</span>
                        <Tag color={cfg.antColor}>{cfg.emoji} {lead.stage}</Tag>
                      </div>
                      <div className="ld-overview-kv">
                        <span>Lead Source</span><strong>{lead.leadSource}</strong>
                      </div>
                      <div className="ld-overview-kv">
                        <span>Assigned To</span><strong>{lead.assignedTo || 'Unassigned'}</strong>
                      </div>
                      <div className="ld-overview-kv">
                        <span>Created</span><strong>{lead.createdAt.toLocaleDateString('en-IN')}</strong>
                      </div>
                      <div className="ld-overview-kv">
                        <span>Last Updated</span><strong>{lead.updatedAt.toLocaleDateString('en-IN')}</strong>
                      </div>
                      <div className="ld-overview-kv">
                        <span>Total Interactions</span>
                        <Badge count={discussions.length} style={{ backgroundColor: '#1E3A8A' }} />
                      </div>
                    </div>
                  ),
                },
              ]}
            />
          </Card>
        </Col>
      </Row>

      {/* ── Stage Confirm Modal ───────────────────────── */}
      <Modal
        title={`Move to ${pendingStage}?`}
        open={stageModalOpen}
        onOk={confirmStageChange}
        onCancel={() => setStageModalOpen(false)}
        okText="Confirm"
        okType={pendingStage === LeadStage.WON ? 'primary' : 'default'}
      >
        <p>
          Are you sure you want to change the stage from{' '}
          <strong>{lead.stage}</strong> → <strong>{pendingStage}</strong>?
        </p>
        {pendingStage === LeadStage.WON && (
          <Alert
            message="This marks the lead as Won and ready for proposal/agreement."
            type="success"
            showIcon
            style={{ marginTop: 12 }}
          />
        )}
      </Modal>

      {/* ── Lost Reason Modal (L-03) ──────────────────── */}
      <Modal
        title="Mark Lead as Lost"
        open={lostModalOpen}
        onCancel={() => setLostModalOpen(false)}
        onOk={() => lostForm.submit()}
        okText="Confirm Lost"
        okButtonProps={{ danger: true }}
      >
        <Alert
          message="This lead will be retained for future reactivation (L-03)."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Form form={lostForm} layout="vertical" onFinish={confirmLostStage}>
          <Form.Item
            name="lostReason"
            label="Lost Reason"
            rules={[{ required: true, message: 'Please select a reason' }]}
          >
            <Select
              placeholder="Why was this lead lost?"
              options={[
                { label: 'Price too high', value: 'price_high' },
                { label: 'Competitor selected', value: 'competitor' },
                { label: 'No budget', value: 'no_budget' },
                { label: 'No requirement now', value: 'no_requirement' },
                { label: 'Not contactable', value: 'not_contactable' },
                { label: 'Technical mismatch', value: 'technical' },
                { label: 'Other', value: 'other' },
              ]}
            />
          </Form.Item>
          <Form.Item name="lostNotes" label="Additional Notes">
            <Input.TextArea
              rows={3}
              placeholder="Any additional context for future reactivation..."
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Reassign Modal ────────────────────────────── */}
      <Modal
        title="Reassign Lead"
        open={reassignModalOpen}
        onCancel={() => setReassignModalOpen(false)}
        footer={null}
      >
        <Form layout="vertical" onFinish={onReassign}>
          <Form.Item
            name="assignedTo"
            label="Assign To"
            rules={[{ required: true, message: 'Select a user' }]}
            initialValue={lead.assignedTo}
          >
            <Select
              size="large"
              options={salesUserOptions}
            />
          </Form.Item>
          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setReassignModalOpen(false)}>Cancel</Button>
              <Button type="primary" htmlType="submit">Reassign</Button>
            </Space>
          </div>
        </Form>
      </Modal>

      {/* ── HTSC / GST Update Modal ───────────────────── */}
      <Modal
        title="Update HTSC & GST Numbers"
        open={gstModalOpen}
        onCancel={() => setGstModalOpen(false)}
        onOk={() => gstForm.submit()}
        okText="Save"
      >
        <Form form={gstForm} layout="vertical" onFinish={onUpdateGst}>
          <Form.Item name="htscNumber" label="HTSC Number">
            <Input placeholder="e.g. 12345" />
          </Form.Item>
          <Form.Item
            name="gstNumber"
            label="GST Number"
            normalize={(v) => (v ? String(v).toUpperCase() : v)}
            rules={[
              {
                validator: (_, v) => (!v || isValidGstFormat(v))
                  ? Promise.resolve()
                  : Promise.reject(new Error('Enter a valid 15-character GST number')),
              },
            ]}
          >
            <Input placeholder="e.g. 33AAACT1234A1Z5" style={{ fontFamily: 'monospace' }} maxLength={15} />
          </Form.Item>
          <Alert
            type="info"
            showIcon
            message="Either field can be left blank — the lead stays usable, and a reminder keeps running until both are filled in."
          />
        </Form>
      </Modal>

      {/* ── WhatsApp Template Modal ───────────────────── */}
      <Modal
        title={<span><WhatsAppOutlined style={{ color: '#25D366', marginRight: 8 }} /> Send WhatsApp Message</span>}
        open={whatsappModalOpen}
        onCancel={() => setWhatsappModalOpen(false)}
        onOk={() => whatsappForm.submit()}
        okText="Send via WhatsApp"
        okButtonProps={{ style: { backgroundColor: '#25D366', borderColor: '#25D366' } }}
        width={600}
      >
        <Form form={whatsappForm} layout="vertical" onFinish={handleWhatsappSend}>
          <Form.Item name="template" label="Select Template">
            <Select onChange={handleWaTemplateChange} options={combinedWaTemplates.map(t => ({ label: t.label, value: t.key }))} />
          </Form.Item>
          <Form.Item
            name="message"
            label="Message Preview & Edit"
            rules={[{ required: true, message: 'Message cannot be empty' }]}
          >
            <Input.TextArea rows={6} placeholder="Type your custom message..." />
          </Form.Item>
          <Form.Item
            name="nextFollowup"
            label="Next Follow-up Date"
            rules={[{ required: true, message: 'Next follow-up date is required' }]}
          >
            <DatePicker style={{ width: '100%' }} format="DD-MM-YYYY" />
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Email Template Modal ──────────────────────── */}
      <Modal
        title={<span><MailOutlined style={{ color: '#2563EB', marginRight: 8 }} /> Send Email</span>}
        open={emailModalOpen}
        onCancel={() => setEmailModalOpen(false)}
        onOk={() => emailForm.submit()}
        okText="Send Email"
        confirmLoading={emailSending}
        width={700}
      >
        <Form form={emailForm} layout="vertical" onFinish={handleEmailSend}>
          <Form.Item name="template" label="Select Template">
            <Select onChange={handleEmailTemplateChange} options={combinedEmailTemplates.map(t => ({ label: t.label, value: t.key }))} />
          </Form.Item>
          <Form.Item
            name="subject"
            label="Subject"
            rules={[{ required: true, message: 'Subject cannot be empty' }]}
          >
            <Input size="large" placeholder="Enter email subject" />
          </Form.Item>
          <Form.Item
            name="body"
            label="Email Body"
            rules={[{ required: true, message: 'Body cannot be empty' }]}
          >
            <Input.TextArea rows={10} placeholder="Type your email body..." />
          </Form.Item>
          <Form.Item
            name="nextFollowup"
            label="Next Follow-up Date"
            rules={[{ required: true, message: 'Next follow-up date is required' }]}
          >
            <DatePicker style={{ width: '100%' }} format="DD-MM-YYYY" />
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Call Log Modal ────────────────────────────── */}
      <Modal
        title={<span><PhoneOutlined style={{ color: '#D97706', marginRight: 8 }} /> Log Customer Call</span>}
        open={callModalOpen}
        onCancel={() => setCallModalOpen(false)}
        onOk={() => callForm.submit()}
        okText="Log Call"
      >
        <Form form={callForm} layout="vertical" onFinish={handleCallLog}>
          <Form.Item
            name="outcome"
            label="Call Outcome"
            rules={[{ required: true, message: 'Please select call outcome' }]}
            initialValue="Connected"
          >
            <Select
              options={[
                { label: '📞 Connected / Answered', value: 'Connected' },
                { label: '⏳ No Answer / Ringing', value: 'No Answer' },
                { label: '🚫 Busy', value: 'Busy' },
                { label: '📴 Switch Off / Not Reachable', value: 'Switch Off' },
                { label: '📅 Callback Scheduled', value: 'Callback Scheduled' },
                { label: '❌ Wrong Number', value: 'Wrong Number' },
              ]}
            />
          </Form.Item>
          <Form.Item
            name="notes"
            label="Call Notes / Summary"
            rules={[{ required: true, message: 'Please write a short summary of the call' }]}
          >
            <Input.TextArea rows={4} placeholder="What was discussed? E.g. Customer requested a follow-up next Monday..." />
          </Form.Item>
          <Form.Item
            name="nextFollowup"
            label="Next Follow-up Date"
            rules={[{ required: true, message: 'Next follow-up date is required' }]}
          >
            <DatePicker style={{ width: '100%' }} format="DD-MM-YYYY" />
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Visit Log Modal ────────────────────────────── */}
      <Modal
        title={<span><EnvironmentOutlined style={{ color: '#0D9488', marginRight: 8 }} /> Log Site Visit</span>}
        open={visitModalOpen}
        onCancel={() => setVisitModalOpen(false)}
        onOk={() => visitForm.submit()}
        okText="Log Visit & Capture Location"
      >
        <Form form={visitForm} layout="vertical" onFinish={handleVisitLog}>
          <Form.Item
            name="notes"
            label="Visit Notes / Summary"
            rules={[{ required: true, message: 'Please write a short summary of the visit' }]}
          >
            <Input.TextArea rows={4} placeholder="What was discussed at the site? E.g. Met with Plant Manager..." />
          </Form.Item>
          <Form.Item
            name="nextFollowup"
            label="Next Follow-up Date"
            rules={[{ required: true, message: 'Next follow-up date is required' }]}
          >
            <DatePicker style={{ width: '100%' }} format="DD-MM-YYYY" />
          </Form.Item>
          <Alert
            message="Your current GPS location will be captured and attached to this visit log for verification."
            type="info"
            showIcon
          />
        </Form>
      </Modal>

      {/* ── Expomeet Visit Modal ───────────────────────── */}
      <Modal
        title={<span><TeamOutlined style={{ color: '#9333EA', marginRight: 8 }} /> Log Expomeet Visit</span>}
        open={expomeetModalOpen}
        onCancel={() => setExpomeetModalOpen(false)}
        onOk={() => expomeetForm.submit()}
        okText="Log Expomeet Visit & Capture Location"
      >
        <Form form={expomeetForm} layout="vertical" onFinish={handleExpomeetLog}>
          <Form.Item
            name="eventName"
            label="Expo / Event Name"
            rules={[{ required: true, message: 'Please enter the expo / event name' }]}
          >
            <Input placeholder="E.g. RenewX 2026, Chennai Trade Centre" />
          </Form.Item>
          <Form.Item
            name="notes"
            label="Meeting Notes / Summary"
            rules={[{ required: true, message: 'Please write a short summary of the meeting' }]}
          >
            <Input.TextArea rows={4} placeholder="What was discussed at the expo? E.g. Visited our stall, interested in 1 MW captive solar..." />
          </Form.Item>
          <Form.Item
            name="nextFollowup"
            label="Next Follow-up Date"
            rules={[{ required: true, message: 'Next follow-up date is required' }]}
          >
            <DatePicker style={{ width: '100%' }} format="DD-MM-YYYY" />
          </Form.Item>
          <Alert
            message="Your current GPS location will be captured and attached to this Expomeet visit for verification."
            type="info"
            showIcon
          />
        </Form>
      </Modal>

      {/* ── Generic Follow-up Modal ───────────────────────── */}
      <Modal
        title={<span><CalendarOutlined style={{ color: '#1E3A8A', marginRight: 8 }} /> Log Follow-up</span>}
        open={followupModalOpen}
        onCancel={() => setFollowupModalOpen(false)}
        onOk={() => followupForm.submit()}
        okText="Save Follow-up"
      >
        <Form form={followupForm} layout="vertical" onFinish={handleFollowupLog}>
          <Form.Item
            name="notes"
            label="Follow-up Action Summary"
            rules={[{ required: true, message: 'Please write a short summary of the action taken' }]}
          >
            <Input.TextArea rows={4} placeholder="What happened during this follow-up? E.g. Client requested revised proposal..." />
          </Form.Item>
          <Form.Item
            name="nextFollowup"
            label="Next Follow-up Date"
            rules={[{ required: true, message: 'Please select the next follow-up date' }]}
          >
            <DatePicker style={{ width: '100%' }} format="DD-MM-YYYY" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default LeadDetail
